import React, { useState } from "react";
import { useApp } from "./context";
import { t, STRINGS } from "./i18n";
import axios from "axios";
import { Plane, Compass, Sparkles, Bike } from "lucide-react";

const ICONS = { transfer: Plane, tour: Compass, cleaning: Sparkles, bikes: Bike };
const IMG = "https://static.prod-images.emergentagent.com/jobs/2155e805-d421-459a-a183-8bf5554e4515/images/696e474069007b8f4f8a36bbb257505ead0edc46579fbc85cfb98b048f33866a.png";

export function ServicesPage() {
  const { lang } = useApp();
  return (
    <div className="section-pad" data-testid="page-services">
      <div className="container-x">
        <div className="overline">{t("nav.services", lang)}</div>
        <h1 className="font-display text-4xl md:text-5xl tracking-tight mt-3">{t("services.title", lang)}</h1>
        <p className="mt-3 max-w-xl" style={{ color: 'var(--text-secondary)' }}>{t("services.sub", lang)}</p>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-10">
          {STRINGS.services.items.map(item => {
            const Icon = ICONS[item.key];
            return (
              <div key={item.key} className="p-8" style={{ background: 'var(--bg-secondary)' }} data-testid={`service-${item.key}`}>
                <Icon size={32} strokeWidth={1.5} style={{ color: 'var(--accent-primary)' }} />
                <h3 className="font-display text-3xl mt-5">{item[lang][0]}</h3>
                <p className="mt-3" style={{ color: 'var(--text-secondary)' }}>{item[lang][1]}</p>
                <div className="mt-5 overline" style={{ color: 'var(--accent-primary)' }}>{item[lang][2]}</div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export function AboutPage() {
  const { lang } = useApp();
  return (
    <div className="section-pad" data-testid="page-about">
      <div className="container-x grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
        <div>
          <div className="overline">{t("about.eyebrow", lang)}</div>
          <h1 className="font-display text-4xl md:text-5xl tracking-tight mt-3">{t("about.title", lang)}</h1>
          <p className="mt-6 text-lg leading-relaxed" style={{ color: 'var(--text-secondary)' }}>{t("about.body", lang)}</p>
        </div>
        <div>
          <img src={IMG} alt="" className="w-full h-auto" />
        </div>
      </div>
    </div>
  );
}

export function FAQPage() {
  const { lang } = useApp();
  const [open, setOpen] = useState(0);
  return (
    <div className="section-pad" data-testid="page-faq">
      <div className="container-x max-w-3xl">
        <div className="overline">{t("nav.faq", lang)}</div>
        <h1 className="font-display text-4xl md:text-5xl tracking-tight mt-3">{t("faq.title", lang)}</h1>
        <div className="mt-12 divide-y" style={{ borderColor: 'var(--border)' }}>
          {STRINGS.faq.items.map((item, i) => {
            const isOpen = open === i;
            const key = item.q_en || `faq-${i}`;
            return (
              <div key={key} className="py-6" style={{ borderTop: '1px solid var(--border)' }} data-testid={`faq-${i}`}>
                <button className="w-full flex justify-between items-center text-left" onClick={() => setOpen(isOpen ? -1 : i)} data-testid={`faq-toggle-${i}`}>
                  <span className="font-display text-2xl">{item[`q_${lang}`]}</span>
                  <span className="text-2xl" style={{ color: 'var(--accent-primary)' }}>{isOpen ? '−' : '+'}</span>
                </button>
                {isOpen && <p className="mt-4 leading-relaxed" style={{ color: 'var(--text-secondary)' }}>{item[`a_${lang}`]}</p>}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export function ContactPage() {
  const { lang, API } = useApp();
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [success, setSuccess] = useState(false);
  const [err, setErr] = useState(null);
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault(); setSending(true); setErr(null);
    try { await axios.post(`${API}/contact`, form); setSuccess(true); }
    catch (e) { setErr(e?.response?.data?.detail || "Error"); }
    finally { setSending(false); }
  };
  return (
    <div className="section-pad" data-testid="page-contact">
      <div className="container-x grid grid-cols-1 md:grid-cols-2 gap-16">
        <div>
          <div className="overline">{t("nav.contact", lang)}</div>
          <h1 className="font-display text-4xl md:text-5xl tracking-tight mt-3">{t("contact.title", lang)}</h1>
          <p className="mt-3 max-w-md" style={{ color: 'var(--text-secondary)' }}>{t("contact.sub", lang)}</p>
          <div className="mt-12 space-y-3" style={{ color: 'var(--text-secondary)' }}>
            <div><div className="overline mb-1">Email</div>hello@stiletresidence.com</div>
            <div><div className="overline mb-1">WhatsApp</div><a href="https://wa.me/38269409419" target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp" style={{ borderBottom: '1px solid currentColor' }}>+382 69 409 419</a></div>
            <div><div className="overline mb-1">Address</div>{t("contact.address", lang)}</div>
          </div>
        </div>
        <div>
          {success ? (
            <div className="p-8" style={{ background: '#E1EFE5', border: '1px solid #1F4A30', color: '#1F4A30' }} data-testid="contact-success">
              {t("contact.success", lang)}
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
              <div>
                <label className="field-label">{t("contact.name", lang)}</label>
                <input className="input-base" value={form.name} onChange={set("name")} required data-testid="contact-name" />
              </div>
              <div>
                <label className="field-label">Email</label>
                <input className="input-base" type="email" value={form.email} onChange={set("email")} required data-testid="contact-email" />
              </div>
              <div>
                <label className="field-label">{t("details.phone", lang)}</label>
                <input className="input-base" value={form.phone} onChange={set("phone")} data-testid="contact-phone" />
              </div>
              <div>
                <label className="field-label">{t("contact.subject", lang)}</label>
                <input className="input-base" value={form.subject} onChange={set("subject")} data-testid="contact-subject" />
              </div>
              <div>
                <label className="field-label">{t("contact.message", lang)}</label>
                <textarea className="input-base" rows={5} value={form.message} onChange={set("message")} required data-testid="contact-message" />
              </div>
              {err && <div style={{ color: '#7A1F1F' }} data-testid="contact-error">{String(err)}</div>}
              <button className="btn-primary" type="submit" disabled={sending} data-testid="contact-submit">{sending ? "..." : t("contact.send", lang)}</button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
